/**
 * Mensagens em português para erros do Firebase Auth na UI.
 * Códigos: https://firebase.google.com/docs/auth/admin/errors
 */

import { FirebaseError } from 'firebase/app';

const MAP: Record<string, string> = {
  'auth/popup-closed-by-user':
    'A janela de login foi fechada antes de concluir. Tente novamente.',
  'auth/cancelled-popup-request':
    'Outro login já está em andamento. Aguarde ou feche as janelas e tente de novo.',
  'auth/popup-blocked':
    'O navegador bloqueou a janela de login. Permita pop-ups para este site e tente novamente.',
  'auth/network-request-failed':
    'Sem conexão com a internet. Verifique e tente novamente.',
  'auth/unauthorized-domain':
    'Este domínio não está autorizado no Firebase. Em Authentication → Settings → Authorized domains, adicione o domínio do site (ex.: localhost em dev).',
  'auth/operation-not-allowed':
    'Este método de login não está ativado. No Console Firebase (Authentication → Sign-in method), ative Google ou Facebook.',
  'auth/account-exists-with-different-credential':
    'Já existe uma conta com este e-mail usando outro método de login. Entre com o método que usou antes.',
  'auth/invalid-credential':
    'Não foi possível validar o login. Tente de novo ou use outro método.',
  'auth/user-disabled':
    'Esta conta foi desativada. Entre em contato com o suporte.',
  'auth/too-many-requests':
    'Muitas tentativas. Aguarde alguns minutos e tente novamente.',
  'auth/invalid-verification-code':
    'Código inválido. Verifique os números e tente de novo.',
  'auth/code-expired':
    'Este código expirou. Peça um novo código.',
  'auth/session-expired':
    'A sessão expirou. Tente novamente.',
  'auth/invalid-app-credential':
    'Configuração de login inválida (app/recaptcha). Verifique o Firebase e o domínio autorizado.',
  'auth/no-current-user':
    'Sua sessão expirou. Faça login novamente para verificar o número.',
  'auth/missing-phone-number':
    'Informe um número de telefone válido.',
  'auth/invalid-phone-number':
    'Número de telefone inválido. Use DDD e o número com código do país (+55).',
  'auth/quota-exceeded':
    'Limite de envios atingido. Tente mais tarde.',
  'auth/internal-error':
    'Erro interno do serviço de login. Tente novamente em instantes.',
};

/**
 * Converte erro do Firebase Auth (ou desconhecido) em mensagem para o usuário.
 */
export function firebaseAuthErrorToUserMessage(error: unknown): string {
  if (error instanceof FirebaseError && error.code && MAP[error.code]) {
    return MAP[error.code];
  }
  if (
    error !== null &&
    typeof error === 'object' &&
    'code' in error &&
    typeof (error as { code: unknown }).code === 'string'
  ) {
    const code = (error as { code: string }).code;
    if (MAP[code]) {
      return MAP[code];
    }
  }
  if (error instanceof Error && error.message.length > 0) {
    const m = error.message;
    const match = /\(auth\/([^)]+)\)/.exec(m);
    if (match?.[1]) {
      const fullCode = `auth/${match[1]}`;
      if (MAP[fullCode]) {
        return MAP[fullCode];
      }
    }
    if (m.startsWith('Firebase:')) {
      return 'Não foi possível concluir o login. Tente novamente.';
    }
    return m;
  }
  return 'Não foi possível concluir a operação. Tente novamente.';
}
